import { useMemo, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Award, Plus, Pencil, Trash2, ExternalLink, Calendar, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { detectTextDirection } from '@/lib/textDirection';
import { MonthYearPicker } from './MonthYearPicker';
import { sortCertificationsNewestFirst } from '@/lib/timelineSorting';
import { formatLocalizedDate } from '@/lib/localizedDate';

interface Certification {
  id: string;
  certification_name: string;
  issuing_organization: string | null;
  issue_date: string | null;
  expiry_date: string | null;
  credential_id: string | null;
  credential_url: string | null;
}

interface Props {
  certifications: Certification[];
  profileId: string;
  onAdd: (data: Record<string, unknown>) => void;
  onUpdate: (id: string, data: Record<string, unknown>) => void;
  onDelete: (id: string) => void;
  saving: boolean;
  editLocked?: boolean;
}

const empty = { certification_name: '', issuing_organization: '', issue_date: '', expiry_date: '', credential_id: '', credential_url: '', no_expiration: false };

function toDateValue(value: string) {
  return value && !/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}-01` : value;
}

function formatDate(dateStr: string | null, language: string): string {
  if (!dateStr) return '';
  try {
    return formatLocalizedDate(dateStr, 'MMM yyyy', language);
  } catch {
    return dateStr;
  }
}

export function CertificationsSection({ certifications, profileId, onAdd, onUpdate, onDelete, saving, editLocked }: Props) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(empty);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const sortedCertifications = useMemo(
    () => sortCertificationsNewestFirst(certifications),
    [certifications],
  );

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (c: Certification) => {
    setEditId(c.id);
    setForm({ certification_name: c.certification_name, issuing_organization: c.issuing_organization ?? '', issue_date: c.issue_date ?? '', expiry_date: c.expiry_date ?? '', credential_id: c.credential_id ?? '', credential_url: c.credential_url ?? '', no_expiration: !c.expiry_date });
    setOpen(true);
  };

  const handleSave = () => {
    const { no_expiration, ...rest } = form;
    const payload = {
      ...rest,
      issue_date: toDateValue(rest.issue_date) || null,
      expiry_date: no_expiration ? null : (toDateValue(rest.expiry_date) || null),
      candidate_profile_id: profileId,
    };
    if (editId) onUpdate(editId, payload);
    else onAdd(payload);
    setOpen(false);
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="flex items-center gap-2.5 text-lg font-bold text-foreground font-[var(--font-display)]">
            <Award className="w-5 h-5 text-seek" />
            {t('seek.profile.certifications')}
          </h3>
          <Button variant="ghost" size="sm" onClick={openNew} disabled={editLocked} className="gap-1 bg-seek/10 text-seek hover:bg-seek hover:text-white">
            <Plus className="w-4 h-4" /> {t('seek.profile.add')}
          </Button>
        </div>

        {certifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-seek/10 flex items-center justify-center mb-3">
              <ShieldCheck className="w-5 h-5 text-seek" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">{t('seek.profile.noCertifications')}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {sortedCertifications.map((c) => (
              <div
                key={c.id}
                className="group relative flex items-start gap-4 rounded-xl border bg-card p-4 hover:border-seek/30 hover:shadow-sm transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-seek/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Award className="w-4.5 h-4.5 text-seek" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground leading-snug" dir={detectTextDirection(c.certification_name)}>{c.certification_name}</p>
                      {c.issuing_organization && (
                        <p className="text-xs font-medium text-seek mt-0.5" dir={detectTextDirection(c.issuing_organization)}>{c.issuing_organization}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c)} disabled={editLocked}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteTarget({ id: c.id, name: c.certification_name })} disabled={editLocked}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {c.issue_date && (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" /> {formatDate(c.issue_date, language)}
                      </span>
                    )}
                    {c.expiry_date ? (
                      <Badge variant="outline" className="text-xs font-normal">
                        {t('seek.profile.expires', { date: formatDate(c.expiry_date, language) })}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs font-normal bg-seek/10 text-seek border-seek/20">
                        {t('seek.profile.noExpiration')}
                      </Badge>
                    )}
                    {c.credential_url && (
                      <a href={c.credential_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-seek hover:underline">
                        <ExternalLink className="w-3 h-3" /> {t('seek.profile.verify')}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? t('seek.profile.editCertification') : t('seek.profile.addCertification')}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>{t('seek.profile.certName')} *</Label><Input value={form.certification_name} onChange={(e) => setForm(p => ({ ...p, certification_name: e.target.value }))} /></div>
            <div><Label>{t('seek.profile.issuingOrg')}</Label><Input value={form.issuing_organization} onChange={(e) => setForm(p => ({ ...p, issuing_organization: e.target.value }))} /></div>
            <div><Label>{t('seek.profile.credentialId')}</Label><Input value={form.credential_id} onChange={(e) => setForm(p => ({ ...p, credential_id: e.target.value }))} /></div>
            <div><Label>{t('seek.profile.issueDate')}</Label><MonthYearPicker value={form.issue_date?.substring(0, 7)} onChange={(v) => setForm(p => ({ ...p, issue_date: v }))} /></div>
            <div>
              <Label>{t('seek.profile.expiryDate')}</Label>
              <MonthYearPicker value={form.expiry_date?.substring(0, 7)} onChange={(v) => setForm(p => ({ ...p, expiry_date: v }))} disabled={form.no_expiration} />
              <div className="flex items-center gap-2 mt-2">
                <Checkbox id="no-expiration" checked={form.no_expiration} onCheckedChange={(checked) => setForm(p => ({ ...p, no_expiration: !!checked, expiry_date: checked ? '' : p.expiry_date }))} className="border-seek data-[state=checked]:bg-seek data-[state=checked]:text-white" />
                <label htmlFor="no-expiration" className="text-xs text-muted-foreground cursor-pointer">{t('seek.profile.noExpiration')}</label>
              </div>
              {form.no_expiration && <p className="mt-1 text-xs text-seek">{t('seek.profile.noExpiryApplied')}</p>}
            </div>
            <div className="sm:col-span-2"><Label>{t('seek.profile.credentialUrl')}</Label><Input value={form.credential_url} onChange={(e) => setForm(p => ({ ...p, credential_url: e.target.value }))} placeholder="https://..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={!form.certification_name || saving} className="bg-seek text-white hover:bg-seek/90">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('seek.profile.deleteCertification')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('seek.profile.deleteCertificationConfirm', { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { onDelete(deleteTarget.id); setDeleteTarget(null); } }}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
